import { useEffect, useState } from "react";
import { Loader2, Search, UserRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { fmtUnits } from "@/lib/loyalty";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const EMPTY = { op: "add", units: "", note: "" };

function CustomerSearch({ open, onPick, mode }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => api.get("/loyalty/customers", { params: { q, all: true } }).then((r) => setList(r.data.slice(0, 30))).catch(() => setList([])), 300);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <div className="space-y-2">
      <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome ou telefone do cliente" className="pl-9" data-testid="scorer-search-input" /></div>
      <div className="max-h-64 divide-y overflow-y-auto rounded-lg border" data-testid="scorer-customer-list">
        {list.map((c) => (
          <button key={c.id} type="button" onClick={() => onPick(c)} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-muted" data-testid={`scorer-customer-${c.id}`}>
            <span className="min-w-0"><span className="block truncate font-medium">{c.name}</span><span className="text-xs text-muted-foreground">{c.phone || "sem telefone"}</span></span>
            <span className="shrink-0 font-mono text-xs">{fmtUnits(mode, c.balance)}</span>
          </button>
        ))}
        {!list.length && <p className="p-4 text-center text-sm text-muted-foreground">Nenhum cliente encontrado.</p>}
      </div>
    </div>
  );
}

export function PointsDialog({ open, onOpenChange, mode, onDone }) {
  const [pick, setPick] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPick(null); setF(EMPTY); } }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/loyalty/manual", { customer_id: pick.id, units: Number(f.units), op: f.op, note: f.note });
      toast.success(f.op === "add" ? "Pontos lançados" : "Pontos removidos");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="scorer-dialog">
        <DialogHeader><DialogTitle>Pontuador</DialogTitle><DialogDescription>Lance ou remova {mode === "cashback" ? "cashback" : "pontos"} de um cliente manualmente.</DialogDescription></DialogHeader>
        {!pick ? <CustomerSearch open={open} onPick={setPick} mode={mode} /> : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border p-3" data-testid="scorer-selected">
              <UserRound className="h-5 w-5 text-muted-foreground" />
              <div className="min-w-0 flex-1"><p className="truncate font-medium">{pick.name}</p><p className="text-xs text-muted-foreground">Saldo: {fmtUnits(mode, pick.balance)}</p></div>
              <Button size="sm" variant="ghost" onClick={() => setPick(null)} data-testid="scorer-change-btn">Trocar</Button>
            </div>
            <div className="grid grid-cols-2 gap-2">{[["add", "Adicionar"], ["remove", "Remover"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, op: k })} data-testid={`scorer-op-${k}`}
                className={cn("rounded-lg border px-3 py-2 text-sm font-medium transition-colors", f.op === k ? (k === "add" ? "border-emerald-500 bg-emerald-500/10 text-emerald-600" : "border-red-500 bg-red-500/10 text-red-500") : "hover:bg-muted")}>{l}</button>
            ))}</div>
            <Field label={mode === "cashback" ? "Valor (R$)" : "Quantidade de pontos"}><Input type="number" min="0" step={mode === "cashback" ? "0.01" : "1"} value={f.units} onChange={(e) => setF({ ...f, units: e.target.value })} data-testid="scorer-units-input" /></Field>
            <Field label="Motivo (opcional)"><Input value={f.note} maxLength={200} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Ex.: pedido feito por telefone" data-testid="scorer-note-input" /></Field>
          </div>
        )}
        {pick && <DialogFooter><Button disabled={busy || !(Number(f.units) > 0)} onClick={save} className="w-full" data-testid="scorer-save-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{f.op === "add" ? "Lançar" : "Remover"}</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
